import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Card, Table, Button, Modal, Form, Row, Col, Tabs, Tab, Pagination } from 'react-bootstrap';
import { fetchExpenses, fetchExpensesSummary, createExpense, updateExpense, deleteExpense } from '../../redux/slices/expensesSlice';
import { fetchCategories } from '../../redux/slices/categoriesSlice';
import { format } from 'date-fns';

const ExpenseList = () => {
  const dispatch = useDispatch();
  const { expenses, summary, pagination, loading } = useSelector((state) => state.expenses);
  const { categories } = useSelector((state) => state.categories);
  const { startDate: periodStart, endDate: periodEnd } = useSelector((state) => state.period);

  const [activeTab, setActiveTab] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [filters, setFilters] = useState({ categoryId: '' });
  const [appliedFilters, setAppliedFilters] = useState({ categoryId: '' });
  const [formData, setFormData] = useState({
    description: '',
    amount: '',
    expense_type: 'variable',
    date: format(new Date(), 'yyyy-MM-dd'),
    category_id: '',
  });

  const typeParam = (tab) => (tab === 'all' ? null : tab);
  const hasActiveFilters = appliedFilters.categoryId;

  const buildFetchParams = (page, tab, activeFilters) => ({
    page,
    type: typeParam(tab),
    categoryId: activeFilters.categoryId || null,
    startDate: periodStart || null,
    endDate: periodEnd || null,
  });

  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  useEffect(() => {
    setCurrentPage(1);
    dispatch(fetchExpenses(buildFetchParams(1, activeTab, appliedFilters)));
    dispatch(fetchExpensesSummary({
      categoryId: appliedFilters.categoryId || null,
      startDate: periodStart || null,
      endDate: periodEnd || null,
    }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, periodStart, periodEnd]);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
    dispatch(fetchExpenses(buildFetchParams(1, tab, appliedFilters)));
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
    dispatch(fetchExpenses(buildFetchParams(page, activeTab, appliedFilters)));
  };

  const handleApplyFilters = () => {
    setAppliedFilters(filters);
    setCurrentPage(1);
    dispatch(fetchExpenses(buildFetchParams(1, activeTab, filters)));
    dispatch(fetchExpensesSummary({
      categoryId: filters.categoryId || null,
      startDate: periodStart || null,
      endDate: periodEnd || null,
    }));
  };

  const handleClearFilters = () => {
    const empty = { categoryId: '' };
    setFilters(empty);
    setAppliedFilters(empty);
    setCurrentPage(1);
    dispatch(fetchExpenses(buildFetchParams(1, activeTab, empty)));
    dispatch(fetchExpensesSummary({
      startDate: periodStart || null,
      endDate: periodEnd || null,
    }));
  };

  const handleOpenModal = (expense = null) => {
    if (expense) {
      setEditingExpense(expense);
      setFormData({
        description: expense.description,
        amount: expense.amount,
        expense_type: expense.expense_type,
        date: expense.date,
        category_id: expense.category_id?.id || '',
      });
    } else {
      setEditingExpense(null);
      setFormData({
        description: '',
        amount: '',
        expense_type: 'variable',
        date: format(new Date(), 'yyyy-MM-dd'),
        category_id: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingExpense(null);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const expenseData = {
      ...formData,
      amount: parseFloat(formData.amount),
      category_id: formData.category_id || undefined,
    };

    if (editingExpense) {
      await dispatch(updateExpense({ id: editingExpense.id, data: expenseData }));
      dispatch(fetchExpenses(buildFetchParams(currentPage, activeTab, appliedFilters)));
    } else {
      await dispatch(createExpense(expenseData));
      handlePageChange(1);
    }
    dispatch(fetchExpensesSummary({
      categoryId: appliedFilters.categoryId || null,
      startDate: appliedFilters.startDate || null,
      endDate: appliedFilters.endDate || null,
    }));
    handleCloseModal();
  };

  const handleDelete = async (id) => {
    if (window.confirm('¿Estás seguro de eliminar este egreso?')) {
      await dispatch(deleteExpense(id));
      dispatch(fetchExpensesSummary({
        categoryId: appliedFilters.categoryId || null,
        startDate: appliedFilters.startDate || null,
        endDate: appliedFilters.endDate || null,
      }));
      const targetPage = expenses.length === 1 && currentPage > 1 ? currentPage - 1 : currentPage;
      handlePageChange(targetPage);
    }
  };

  const renderPagination = () => {
    if (pagination.pages <= 1) return null;
    const items = [];
    for (let i = 1; i <= pagination.pages; i++) {
      items.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i}
        </Pagination.Item>
      );
    }
    return (
      <div className="d-flex justify-content-between align-items-center mt-3">
        <small className="text-muted">
          Mostrando {expenses.length} de {pagination.total} registros
        </small>
        <Pagination className="mb-0">
          <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 1} />
          {items}
          <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage === pagination.pages} />
        </Pagination>
      </div>
    );
  };

  const fixedCount = summary?.fixed_expenses?.length ?? 0;
  const variableCount = summary?.variable_expenses?.length ?? 0;

  if (loading) {
    return <Container className="mt-4"><div className="text-center">Cargando...</div></Container>;
  }

  const ExpenseTable = () => (
    <>
      <Table striped bordered hover responsive>
        <thead>
          <tr>
            <th>Descripción</th>
            <th>Monto</th>
            <th>Categoría</th>
            <th>Fecha</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {expenses.length === 0 ? (
            <tr>
              <td colSpan="5" className="text-center">No hay egresos registrados</td>
            </tr>
          ) : (
            expenses.map((expense) => (
              <tr key={expense.id}>
                <td>{expense.description}</td>
                <td className="text-danger">${expense.amount.toFixed(2)}</td>
                <td style={{ backgroundColor: expense.category_id?.color || 'transparent' }}>
                  {expense.category_id?.name || 'Sin categoría'}
                </td>
                <td>{format(new Date(expense.date + 'T00:00:00'), 'dd/MM/yyyy')}</td>
                <td>
                  <Button variant="warning" size="sm" className="me-2" onClick={() => handleOpenModal(expense)}>
                    Editar
                  </Button>
                  <Button variant="danger" size="sm" onClick={() => handleDelete(expense.id)}>
                    Eliminar
                  </Button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
      {renderPagination()}
    </>
  );

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col><h2>Egresos</h2></Col>
        <Col className="text-end">
          <Button variant="danger" onClick={() => handleOpenModal()}>Nuevo Egreso</Button>
        </Col>
      </Row>

      <Row className="mb-4">
        <Col md={4}>
          <Card bg="danger" text="white">
            <Card.Body>
              <Card.Title>Gastos Fijos</Card.Title>
              <h3>${summary?.total_fixed?.toFixed(2) || '0.00'}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card bg="warning" text="white">
            <Card.Body>
              <Card.Title>Gastos Variables</Card.Title>
              <h3>${summary?.total_variable?.toFixed(2) || '0.00'}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col md={4}>
          <Card bg="dark" text="white">
            <Card.Body>
              <Card.Title>Total Egresos</Card.Title>
              <h3>${summary?.total_expenses?.toFixed(2) || '0.00'}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Card className="mb-3">
        <Card.Body>
          <Row className="g-2 align-items-end">
            <Col md={6}>
              <Form.Label className="mb-1 fw-semibold">Categoría</Form.Label>
              <Form.Select
                value={filters.categoryId}
                onChange={(e) => setFilters({ ...filters, categoryId: e.target.value })}
              >
                <option value="">Todas las categorías</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>{cat.name}</option>
                ))}
              </Form.Select>
            </Col>
            <Col md={3} className="d-flex gap-2 align-items-end">
              <Button variant="danger" onClick={handleApplyFilters} className="flex-grow-1">
                Filtrar
              </Button>
              {hasActiveFilters && (
                <Button variant="outline-secondary" onClick={handleClearFilters} title="Limpiar filtros">
                  ✕
                </Button>
              )}
            </Col>
            {(periodStart || periodEnd) && (
              <Col md={3} className="d-flex align-items-end">
                <small className="text-muted">
                  Período: <strong>{periodStart || '…'}</strong> → <strong>{periodEnd || '…'}</strong>
                </small>
              </Col>
            )}
          </Row>
          {hasActiveFilters && (
            <div className="mt-2">
              <small className="text-muted">
                Categoría activa:&nbsp;
                <span className="badge bg-secondary">
                  {categories.find((c) => String(c.id) === String(appliedFilters.categoryId))?.name || 'Categoría'}
                </span>
              </small>
            </div>
          )}
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <Tabs activeKey={activeTab} onSelect={handleTabChange} className="mb-3">
            <Tab eventKey="all" title={`Todos (${fixedCount + variableCount})`}>
              <ExpenseTable />
            </Tab>
            <Tab eventKey="fixed" title={`Fijos (${fixedCount})`}>
              <ExpenseTable />
            </Tab>
            <Tab eventKey="variable" title={`Variables (${variableCount})`}>
              <ExpenseTable />
            </Tab>
          </Tabs>
        </Card.Body>
      </Card>

      <Modal show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{editingExpense ? 'Editar Egreso' : 'Nuevo Egreso'}</Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                type="text" name="description" value={formData.description}
                onChange={handleChange} placeholder="Ej: Luz, Agua, Teléfono, etc." required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Monto</Form.Label>
              <Form.Control
                type="number" step="0.01" name="amount" value={formData.amount}
                onChange={handleChange} placeholder="0.00" required
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Tipo de Gasto</Form.Label>
              <Form.Select name="expense_type" value={formData.expense_type} onChange={handleChange} required>
                <option value="fixed">Fijo</option>
                <option value="variable">Variable</option>
              </Form.Select>
              <Form.Text className="text-muted">
                Fijo: gastos recurrentes (alquiler, servicios). Variable: gastos ocasionales.
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Fecha</Form.Label>
              <Form.Control type="date" name="date" value={formData.date} onChange={handleChange} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Categoría (Opcional)</Form.Label>
              <Form.Select name="category_id" value={formData.category_id} onChange={handleChange}>
                <option value="">Sin categoría</option>
                {categories.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseModal}>Cancelar</Button>
            <Button variant="danger" type="submit">{editingExpense ? 'Actualizar' : 'Crear'}</Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
};

export default ExpenseList;
